export type StatusLevel = 'critical' | 'warn' | 'watch' | 'healthy' | 'info';

export type StatusPillProps = {
  level: StatusLevel;
  label: string;
  variant?: 'solid' | 'outlined';
  icon?: React.ReactNode;
};

// Per-level pairs that all clear WCAG AA at 11px:
//   - critical / info use the matching colored text on the tinted bg
//     (already AA-clear).
//   - warn / watch / healthy use the `*-fg` darker companion tokens
//     introduced in this PR so the same hue can carry the label legibly.
const SOLID: Record<StatusLevel, { bg: string; fg: string }> = {
  critical: { bg: 'bg-critical-bg', fg: 'text-critical' },
  warn: { bg: 'bg-warn-bg', fg: 'text-warn-fg' },
  watch: { bg: 'bg-watch-bg', fg: 'text-watch-fg' },
  healthy: { bg: 'bg-healthy-bg', fg: 'text-healthy-fg' },
  info: { bg: 'bg-info-bg', fg: 'text-info' },
};

const OUTLINED: Record<StatusLevel, string> = {
  critical: 'border-critical',
  warn: 'border-warn',
  watch: 'border-watch',
  healthy: 'border-healthy',
  info: 'border-info',
};

// Backend status vocabularies don't always match StatusLevel exactly (e.g.
// asset currentStatus carries "warning" / "monitor"). Normalize known aliases
// and fall back to a neutral tone for anything unrecognized so a single stray
// value can never throw `Cannot read properties of undefined` and white-screen
// the whole route via the error boundary.
const LEVEL_ALIASES: Record<string, StatusLevel> = {
  warning: 'warn',
  monitor: 'watch',
  degraded: 'warn',
  offline: 'critical',
  online: 'healthy',
  ok: 'healthy',
  normal: 'healthy',
};

function normalizeLevel(level: string): StatusLevel {
  if (level in SOLID) return level as StatusLevel;
  return LEVEL_ALIASES[level] ?? 'info';
}

/**
 * Compact severity badge. Never color-only: text label is mandatory; the
 * tinted background reinforces the hue. With the `*-fg` companion tokens
 * the solid variant no longer needs a leading dot to clear contrast.
 */
export function StatusPill({
  level,
  label,
  variant = 'solid',
  icon,
}: StatusPillProps) {
  const base =
    'inline-flex items-center gap-sp-1 rounded-r-chip px-sp-2 py-sp-1 text-fs-meta font-medium';

  // level is typed StatusLevel, but real backends send aliases ("warning",
  // "monitor", ...). Normalize at the boundary so the color lookups below are
  // always defined.
  const safeLevel = normalizeLevel(level as string);

  if (variant === 'solid') {
    const tone = SOLID[safeLevel];
    return (
      <span
        className={`${base} ${tone.bg} ${tone.fg}`}
        data-level={safeLevel}
        data-variant="solid"
      >
        {icon ? (
          <span aria-hidden className="inline-flex">
            {icon}
          </span>
        ) : null}
        <span>{label}</span>
      </span>
    );
  }

  return (
    <span
      className={`${base} border bg-transparent text-gray-700 ${OUTLINED[safeLevel]}`}
      data-level={safeLevel}
      data-variant="outlined"
    >
      {icon ? (
        <span aria-hidden className="inline-flex">
          {icon}
        </span>
      ) : null}
      <span>{label}</span>
    </span>
  );
}
